import React from "react";
import {
  Row,
  Button,
  Col,
  ButtonDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { Controller } from "react-hook-form";
import Select from "react-select";
import { timeDifference } from "utils/timeDifference";
import { useEffect, useState, useMemo, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useQuery } from "react-query";

import {
  CustomModal,
  CustomAlert,
  FormEntry,
  FormSelect,
  FormCheck,
  ProtectedImg,
  useStudentReportPDF,
} from "components";
import apiService from "services/api";
import { models, permission, permissionCheck } from "utils/permissionCheck";
import moment from "moment";
import { formatGroupLabel } from "../constants";
import { centreOptions } from "constants";
import useSelectLessonModal, { sortLessonRecords } from "../SelectLessonModal";
import { FiSearch } from "react-icons/fi";
import useStudentProfileModal from "./StudentProfileModal";
import { FormGroup } from "../NewEventModal";
import useSelectTeacherModal from "../SelectTeacherModal";
import { AddHomeworkModal } from "../AddHomeworkModal";
import {
  addHomeworkModalActions,
  initialAddHomeworkModalState,
  addHomeworkModalReducer,
} from "../reducers";

const LessonDetailPageFormGroup = ({
  children,
  formLabel,
  error,
  light = false,
  editable = false,
  customEndComponent,
  alt,
}) => (
  <>
    <div className="d-flex justify-content-end">
      {editable && customEndComponent}
    </div>
    <div className="mb-3 row align-items-center">
      <div
        className={`${light && "text-dark"} text-left col-sm-4 col-lg-3 mb-1 text-light font-weight-bold`}
      >
        {formLabel}
      </div>
      {editable ? (
        <div className="col-sm-8 col-lg-9">{children}</div>
      ) : (
        <div className="col-sm-8 col-lg-9 text-info">{alt}</div>
      )}
      {error && (
        <div className="text-center">
          <small className="text-danger">{error?.message}</small>
        </div>
      )}
    </div>
  </>
);

export const LessonDetailPage = ({
  updateLessonRecord,
  selectedCourse,
  refetchEvents,
  open,
  dirtyFields,
  hide,
  editable = false,
  event,
  register,
  errors,
  watch,
  teacherOptions,
  refetchSingleLessonRecord,
  isFetchingSingleLessonRecord,
  studentOptions,
  courseOptions,
  lessonOptions,
  submitLoading,
  control,
  selectedStudentsData,
  setValue,
  deleteLessonRecord,
  toggleEdit,
  setShowDeleteAlert,
  showStudentReportModal,
  reset,
  getValues,
  showDeleteAlert,
  openAttendanceModal = () => {},
  legacyCompletedLessons,
  completedLessons,
  isCustomLesson,
  isStudentCourseFetching,
  setCustomLesson = () => {},
}) => {
  const [studentReportDropdownOpen, setStudentReportDropdownOpen] =
    useState(false);
  const toggleStudentReportDropdownOpen = () =>
    setStudentReportDropdownOpen((state) => !state);

  const isClassOver =
    moment(event?.date ? event?.date + " " + event?.startTime : undefined).diff(
      moment(),
    ) <
    30 * 1000 * 60;

  const isAssignedTeacher =
    apiService.auth?.token?.body()?.id === event.teacher?.id;

  const isEditable = !event?.students?.some(
    (student) => student.studentAttendanceRecorded,
  );

  const selectedLesson = watch("lesson");
  const [date, startTime, endTime, teacher] = watch([
    "date",
    "startTime",
    "endTime",
    "teacher",
  ]);
  const homeworkTime = watch("homeworkTime");

  const selectedCustomLesson = watch("customLessonName");

  const isDeletable =
    !event?.students?.some((student) => student.studentAttendanceRecorded) &&
    permissionCheck(apiService.auth.token.get(), models.LESSON_RECORD, [
      permission.DELETE,
    ]);

  const isAssigner = apiService.auth?.token?.body()?.id === event.assigner?.id;

  const hasEditPermissions =
    permissionCheck(
      apiService.auth.token.get(),
      models.LESSON_RECORD,
      [permission.ADMIN_EDIT_PERMISSION, permission.UPDATE],
      "or",
    ) || apiService.auth?.token?.body()?.role?.centre?.includes(event.centre);

  const isAttendanceRecorded =
    event?.students?.some((student) => student.studentAttendanceRecorded) &&
    event?.teacherAttendanceRecorded &&
    (event?.rawObservers?.length
      ? event?.rawObservers?.some((observer) => observer.attendanceRecorded)
      : true);

  const isAbleToRecordAttendance = permissionCheck(
    apiService.auth.token.get(),
    models.LESSON_RECORD,
    [permission.ADMIN_EDIT_PERMISSION],
  );

  const isStudentLate = watch("isStudentLate");

  const [studentToView, setStudentToView] = useState(0);

  const [PDFView, previewPDF, exportStudentReportAsPdf, exportingPDF] =
    useStudentReportPDF({
      studentReport: event?.students?.[studentToView]?.studentReport,
      student: event?.students?.[studentToView]?.studentDetail,
    });

  useEffect(() => {
    setValue("studentIds", event.studentIds);
  }, [studentOptions]);

  const {
    register: deleteRecurredRegister,
    getValues: getValuesForRecurredRegister,
  } = useForm({ defaultValues: { deleteRecurred: false } });

  const [SelectLessonModal, selectLessonModalState, selectLessonModalDispatch] =
    useSelectLessonModal();
  const [SelectTeacherModal, _, selectTeacherModalDispatch] =
    useSelectTeacherModal({ date, startTime, endTime });

  const { data: availableTeachers, isLoading: isLoadingAvailableTeachers } =
    useQuery(
      ["availability", "lesson", startTime, endTime, date],
      () =>
        apiService.staff.availability.get(undefined, {
          start_time: startTime,
          end_time: endTime,
          selected_date: date,
        }), //queryParams are camelcased
      { enabled: !!(date && endTime && startTime) },
    );

  const {
    StudentProfileModal,
    controls: studentProfileModalControls,
    viewStudent,
  } = useStudentProfileModal(selectedStudentsData, selectedCourse?.id);

  const sortedLessonRecords = useMemo(
    () =>
      sortLessonRecords([
        ...completedLessons.records,
        ...legacyCompletedLessons.records,
      ]).filter((record) => record.name),
    [completedLessons.records, legacyCompletedLessons.records],
  );

  const incrementLessonEndTime = useCallback((duration) => {
    const newTime = moment(getValues("endTime"), "HH:mm:ss")
      .add(duration, "minutes")
      .format("HH:mm:ss");
    setValue("endTime", newTime);
  }, []);

  useEffect(() => {
    if (!dirtyFields.homeworkTime) return;
    if (homeworkTime) {
      setValue("homeworkTimeDuration", "15");
      incrementLessonEndTime(15);
    } else if (homeworkTime == false) {
      incrementLessonEndTime(-getValues("homeworkTimeDuration"));
      setValue("homeworkTimeDuration", undefined);
    }
  }, [homeworkTime]); // so that the homeworktime does not affect if not checked before

  const sortedCustomRecords = useMemo(
    () =>
      sortLessonRecords(
        sortLessonRecords(completedLessons.customRecords),
      ).filter((record) => record.name),
    [completedLessons.customRecords],
  );

  const isHomeworkAdded = event?.students?.some(
    (student) => !!student.lessonHomework,
  );
  const [addHomeworkModalProps, dispatchAddHomeworkModalProps] =
    React.useReducer(addHomeworkModalReducer, initialAddHomeworkModalState);

  const showAddHomeworkModal = (lessonDetail) => {
    dispatchAddHomeworkModalProps({
      type: addHomeworkModalActions.OPEN,
      payload: lessonDetail,
    });
  };

  return (
    <>
      <AddHomeworkModal
        open={addHomeworkModalProps.show}
        lessonDetail={event}
        refetch={refetchEvents}
        refetchSingleLessonRecord={refetchSingleLessonRecord}
        hide={() =>
          dispatchAddHomeworkModalProps({
            type: addHomeworkModalActions.HIDE,
          })
        }
      />
      <form onSubmit={updateLessonRecord}>
        <PDFView />
        <StudentProfileModal />
        <SelectTeacherModal
          errors={errors}
          control={control}
          availableTeachers={availableTeachers}
          date={date}
          startTime={startTime}
          endTime={endTime}
          teacherOptions={teacherOptions}
        />
        <SelectLessonModal
          sortedLessonRecords={sortedLessonRecords}
          sortedCustomRecords={sortedCustomRecords}
          errors={errors}
          setCustomLessonEntry={setCustomLesson}
          isCustomLessonEntry={isCustomLesson}
          register={register}
          control={control}
          lessonOptions={lessonOptions}
          isStudentCourseFetching={isStudentCourseFetching}
          formatGroupLabel={formatGroupLabel}
        />
        <CustomAlert
          show={showDeleteAlert}
          description={
            <>
              Lesson Record on{" "}
              <span className="font-weight-bold text-info">
                [{event?.date}]
              </span>{" "}
              from{" "}
              <span className="font-weight-bold text-info">
                [{event?.startTime}
              </span>{" "}
              to{" "}
              <span className="font-weight-bold text-info">
                {event?.endTime}]
              </span>{" "}
              for{" "}
              <span className="font-weight-bold text-info">
                {event?.students?.map((student, index) =>
                  index > 0
                    ? " and "
                    : "" +
                      student.studentDetail?.firstName +
                      " " +
                      student.studentDetail?.lastName +
                      " ",
                )}
              </span>
              {event?.recurrencePointerId && (
                <div className="mt-3">
                  <FormCheck
                    formLabel="Delete all recurred events?"
                    register={deleteRecurredRegister}
                    name="deleteRecurred"
                    error={errors.deleteRecurred}
                  />
                </div>
              )}
            </>
          }
          submitButtonText="Delete"
          submitModal={() =>
            deleteLessonRecord(getValuesForRecurredRegister("deleteRecurred"))
          }
          title="Confirm delete event?"
          hideModal={() => setShowDeleteAlert(false)}
        />

        <CustomModal
          show={open}
          hideModal={hide}
          title="Lesson Detail"
          light={true}
          buttons={
            <>
              {!editable &&
                isClassOver &&
                isAssignedTeacher &&
                !event.isCancelled && (
                  <>
                    <Button
                      color="info"
                      type="button"
                      onClick={() => showAddHomeworkModal(event)}
                      disabled={
                        !isClassOver ||
                        !isAttendanceRecorded ||
                        isFetchingSingleLessonRecord
                      }
                    >
                      {isHomeworkAdded ? "View Homework" : "Add Homework"}
                    </Button>
                    <Button
                      color="info"
                      type="button"
                      onClick={() => showStudentReportModal(event)}
                      disabled={
                        !isClassOver ||
                        !isAttendanceRecorded ||
                        event?.students?.every(
                          (student) => student.studentReportAdded,
                        ) ||
                        isFetchingSingleLessonRecord
                      }
                    >
                      Add Student Report
                    </Button>
                  </>
                )}
              {!editable &&
                !event.isCancelled &&
                (isAbleToRecordAttendance || isAssignedTeacher) && (
                  <Button
                    onClick={() => openAttendanceModal(event)}
                    color="success"
                    className="d-inline-block"
                    disabled={
                      !isClassOver ||
                      isAttendanceRecorded ||
                      isFetchingSingleLessonRecord
                    }
                  >
                    Attendance
                  </Button>
                )}
            </>
          }
        >
          {(isAssigner || hasEditPermissions) && (
            <div className="d-flex justify-content-end mb-3">
              {!editable && (
                <>
                  <Button
                    color="info"
                    size="sm"
                    type="button"
                    onClick={toggleEdit}
                  >
                    Edit
                  </Button>
                  {isDeletable && (
                    <Button
                      color="danger"
                      size="sm"
                      type="button"
                      onClick={() => setShowDeleteAlert(true)}
                    >
                      Delete
                    </Button>
                  )}
                </>
              )}
              {editable && (
                <>
                  <Button
                    color="warning"
                    size="sm"
                    type="submit"
                    disabled={submitLoading}
                  >
                    Save
                  </Button>
                  <Button
                    color="info"
                    size="sm"
                    type="button"
                    onClick={() => reset(event)}
                    disabled={submitLoading}
                  >
                    Reset
                  </Button>
                  <Button
                    color="cancel"
                    size="sm"
                    type="button"
                    onClick={toggleEdit}
                    disabled={submitLoading}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </div>
          )}
          {!editable && event.isCancelled && (
            <h4 className="text-danger ml-2 font-weight-bold">
              Class Cancelled
            </h4>
          )}
          <Row className="align-items-center">
            <Col sm={12} xs={12} className="d-flex justify-content-end">
              <FormCheck
                register={register}
                name="isCancelled"
                formLabel="Cancel Class?"
                error={errors.isCancelled}
                editable={editable}
              />
            </Col>

            <Col lg={6}>
              <FormEntry
                formLabel="Lesson Date"
                register={register}
                additionalOptions={{ required: true }}
                name="date"
                type="date"
                required={true}
                readOnly={!editable}
              />
            </Col>
            <Col lg={6}>
              <FormEntry
                formLabel="Start Time"
                register={register}
                name="startTime"
                type="time"
                error={errors.startTime}
                required={true}
                readOnly={!editable}
                step="60"
              />
            </Col>
            <Col lg={6}>
              <FormEntry
                formLabel="End Time"
                register={register}
                name="endTime"
                type="time"
                validation={{
                  validate: {
                    checkMinMax: (preparationEndTime) =>
                      timeDifference(
                        getValues("startTime"),
                        preparationEndTime,
                      ) > 0 || "End time must be after start time",
                  },
                }}
                error={errors.endTime}
                required={true}
                readOnly={!editable}
                step="60"
              />
            </Col>
            <Col lg={6}>
              <div className="d-flex align-items-center" style={{ gap: 10 }}>
                <FormCheck
                  formLabel="Late?"
                  register={register}
                  name="isStudentLate"
                  editable={editable}
                  altStatus={isStudentLate}
                />
                <FormCheck
                  formLabel="Urgent Class?"
                  register={register}
                  name="isUrgentClass"
                  editable={editable}
                  altStatus={watch("isUrgentClass")}
                />
                <FormCheck
                  formLabel="Homework Time"
                  register={register}
                  name="homeworkTime"
                  editable={editable}
                  altStatus={homeworkTime}
                />
                <FormCheck
                  formLabel="Driver/Taxi Sent?"
                  register={register}
                  name="isDriverSent"
                  editable={editable}
                  altStatus={watch("isDriverSent")}
                />
              </div>
            </Col>

            <Col sm={6}>
              <div className="d-flex">
                {homeworkTime && editable && (
                  <Row className="ml-2">
                    <Col>
                      <Controller
                        control={control}
                        name="homeworkTimeDuration"
                        render={({
                          field: { value, onChange },
                          fieldState: { error },
                        }) => (
                          <FormGroup width="w-auto" error={error}>
                            <input
                              type="radio"
                              onChange={(e) => {
                                incrementLessonEndTime(
                                  -value + +e.target.value,
                                );
                                onChange(e.target.value);
                              }}
                              checked={value == "15"}
                              value="15"
                            />
                            <small className="text-primary ml-1">15m</small>
                          </FormGroup>
                        )}
                      />
                    </Col>
                    <Controller
                      control={control}
                      name="homeworkTimeDuration"
                      render={({
                        field: { value, onChange },
                        fieldState: { error },
                      }) => (
                        <FormGroup width="w-auto" error={error}>
                          <input
                            type="radio"
                            onChange={(e) => {
                              incrementLessonEndTime(-value + +e.target.value);
                              onChange(e.target.value);
                            }}
                            checked={value == "30"}
                            value="30"
                          />
                          <small className="text-primary ml-1">30m</small>
                        </FormGroup>
                      )}
                    />
                  </Row>
                )}
              </div>
            </Col>
            <Col sm={12}>
              {isStudentLate && (
                <Row>
                  <Col>
                    <FormEntry
                      formLabel="Late Arrival Time"
                      register={register}
                      name="studentLateArrivalTime"
                      type="time"
                      validation={{
                        validate: {
                          checkMinMax: (studentLateArrivalTime) =>
                            timeDifference(
                              getValues("startTime"),
                              studentLateArrivalTime,
                            ) > 0 ||
                            "Student Late Arrival Time must be more than start time",
                        },
                      }}
                      error={errors.studentLateArrivalTime}
                      required={true}
                      readOnly={!editable}
                      step="60"
                    />
                  </Col>
                  <Col>
                    <FormEntry
                      formLabel="Late Leave Time"
                      register={register}
                      name="studentLateLeaveTime"
                      type="time"
                      validation={{
                        validate: {
                          checkMinMax: (studentLateLeaveTime) =>
                            timeDifference(
                              getValues("startTime"),
                              studentLateLeaveTime,
                            ) > 0 ||
                            "Student Late Arrival Time must be more than start time",
                          checkStartTimeDiff: (studentLateLeaveTime) =>
                            timeDifference(
                              getValues("studentLateArrivalTime"),
                              studentLateLeaveTime,
                            ) > 0 ||
                            "Student Late Leave Time must be after Student Late Arrival time",
                        },
                      }}
                      error={errors.studentLateLeaveTime}
                      required={true}
                      readOnly={!editable}
                      step="60"
                    />
                  </Col>
                </Row>
              )}
            </Col>
          </Row>

          <Row>
            <Col lg={6}>
              <FormSelect
                formLabel="Centre"
                register={register}
                name="centre"
                error={errors.centre}
                options={[
                  ...centreOptions,
                  { label: "Online", value: "Online", disabled: true },
                ]}
                required={true}
                readOnly={!editable}
              />
            </Col>
            <Col lg={6}>
              <FormEntry
                formLabel="Location (Room)"
                register={register}
                name="location"
                type="text"
                error={errors.location}
                readOnly={!editable}
              />
            </Col>
          </Row>

          <FormEntry
            formLabel="Remark"
            register={register}
            name="remark"
            type="text"
            error={errors.remark}
            readOnly={!editable}
          />

          <FormCheck
            formLabel="Trial Class?"
            register={register}
            name="isTrialClass"
            editable={editable}
            altStatus={watch("isTrialClass")}
          />

          <div className="mt-5 ml-1 ml-sm-2">
            <div className="row mb-3">
              <div className="col-sm-4 col-lg-3 text-left mb-1 text-light font-weight-bold">
                Assigner
              </div>
              <div className="col-sm-8 col-lg-9 text-info">
                {event?.assigner?.fullName}
              </div>
            </div>
            <LessonDetailPageFormGroup
              error={errors.studentIds}
              formLabel="Student"
              editable={editable}
              alt={
                <div className="d-flex">
                  {event?.students?.map((student) => (
                    <span
                      className="d-flex align-items-center border border-info pl-2 pr-1 rounded"
                      style={{ columnGap: 4 }}
                      key={student.studentDetail?.id}
                    >
                      {student.studentDetail?.firstName +
                        " " +
                        student.studentDetail?.lastName}{" "}
                      <FiSearch
                        style={{ cursor: "pointer" }}
                        onClick={() => viewStudent(student.studentDetail?.id)}
                      />
                    </span>
                  ))}
                </div>
              }
            >
              <Controller
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: "Student is required",
                  },
                }}
                defaultValue={[]}
                name="studentIds"
                render={({ field: { onChange, value, ref } }) => (
                  <Select
                    inputRef={ref}
                    className="react-select info"
                    classNamePrefix="react-select"
                    value={studentOptions?.filter((c) =>
                      value.map((v) => v.value || v).includes(c.value),
                    )}
                    onChange={(val) => {
                      onChange(val?.map((val) => val.value) || []);
                      setValue("course", null);
                      setValue("lesson", null);
                    }}
                    key={JSON.stringify(value)}
                    isClearable={false}
                    options={studentOptions || []}
                    isMulti
                    placeholder="Select student...."
                  />
                )}
              />
            </LessonDetailPageFormGroup>
            <LessonDetailPageFormGroup
              error={errors.course}
              formLabel="Course"
              editable={editable}
              alt={event?.course?.name}
            >
              <Controller
                control={control}
                name="course"
                rules={{
                  required: {
                    value: true,
                    message: "Add a course",
                  },
                }}
                render={({ field: { onChange, value, ref } }) => (
                  <Select
                    inputRef={ref}
                    className="react-select info"
                    classNamePrefix="react-select"
                    key={JSON.stringify(value)}
                    value={courseOptions?.find((c) => {
                      return c.value === value?.id;
                    })}
                    onChange={(val) => {
                      onChange(val.data);
                      if (val.value !== value.id)
                        return setValue("lesson", null);
                    }}
                    placeholder="Select course...."
                    options={courseOptions || []}
                    isDisabled={isStudentCourseFetching}
                  />
                )}
              />
            </LessonDetailPageFormGroup>
            <LessonDetailPageFormGroup
              error={errors.lesson}
              formLabel="Lesson"
              editable={editable}
              alt={event?.lesson?.name}
            >
              <button
                className="btn btn-sm btn-info"
                type="button"
                size="sm"
                disabled={!lessonOptions}
                onClick={() => selectLessonModalDispatch({ type: "OPEN" })}
              >
                Select Lesson
              </button>
              <span className="ml-2 text-info">
                {selectedCustomLesson || selectedLesson?.name}
              </span>
            </LessonDetailPageFormGroup>
            <LessonDetailPageFormGroup
              error={errors.teacher}
              formLabel="Teacher"
              editable={editable}
              alt={event?.teacher?.fullName}
            >
              <button
                className="btn btn-sm btn-info"
                type="button"
                size="sm"
                disabled={isLoadingAvailableTeachers}
                onClick={() => selectTeacherModalDispatch({ type: "OPEN" })}
              >
                Select Teacher
              </button>
              <span className="ml-2 text-info">{teacher?.fullName}</span>
            </LessonDetailPageFormGroup>
            <LessonDetailPageFormGroup
              error={errors.observers}
              formLabel="Observer"
              editable={editable}
              alt={event?.observers?.map(
                (observer, index) =>
                  observer?.fullName +
                  (index !== event?.observers?.length - 1 ? ", " : ""),
              )}
            >
              <Controller
                control={control}
                defaultValue={[]}
                name="observers"
                render={({ field: { onChange, value, ref } }) => (
                  <Select
                    inputRef={ref}
                    className="react-select info"
                    classNamePrefix="react-select"
                    value={teacherOptions?.filter((c) =>
                      value
                        .map((v) => v?.id || v?.value || v)
                        .includes(c.value.id),
                    )}
                    onChange={(val) => {
                      onChange(val?.map((val) => val.value) || []);
                    }}
                    key={JSON.stringify(value)}
                    isClearable={false}
                    options={teacherOptions || []}
                    isMulti
                    placeholder="Select observers...."
                  />
                )}
              />
            </LessonDetailPageFormGroup>
            {event?.students?.some((student) => student.studentReport) &&
              (event?.students?.length > 1 ? (
                <div className="d-flex justify-content-end">
                  <ButtonDropdown
                    isOpen={studentReportDropdownOpen}
                    toggle={toggleStudentReportDropdownOpen}
                  >
                    <DropdownToggle caret color="info" size="sm">
                      View Student Report
                    </DropdownToggle>
                    <DropdownMenu>
                      {event?.students?.map((student, index) => (
                        <DropdownItem
                          onClick={() => {
                            setStudentToView(index);
                            previewPDF();
                          }}
                          key={index}
                        >
                          {student.studentDetail.firstName}{" "}
                          {student.studentDetail.lastName}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  </ButtonDropdown>
                </div>
              ) : (
                <div className="d-flex justify-content-end">
                  <Button
                    color="info"
                    type="button"
                    onClick={previewPDF}
                    className="d-inline-block"
                  >
                    View Student Report
                  </Button>
                </div>
              ))}
          </div>
          {isAttendanceRecorded && (
            <div className="row justify-content-between mt-3">
              <div className="col-md-4 col-12 col-sm-6 mb-2 text-white">
                {event.teachersRemark && (
                  <>
                    <div className="text-underline">Teachers Remark</div>
                    <small>{event.teachersRemark}</small>
                  </>
                )}
              </div>
              <div className="d-flex col-md-4 col-12 col-sm-6 mb-2">
                <div className="text-center mx-1">
                  <ProtectedImg src={event.teacherSignaturePath} />
                  <small className="text-white d-block">
                    {event?.teacher?.fullName} (Teacher)
                  </small>
                </div>

                {event.students.map((student, index) => (
                  <div className="text-center mx-1" key={index}>
                    <ProtectedImg src={student.studentSignaturePath} />
                    <small className="text-white d-block">
                      {student?.studentDetail?.firstName}{" "}
                      {student?.studentDetail?.lastName}
                    </small>
                  </div>
                ))}

                {event.rawObservers?.map((observer, index) => (
                  <div className="text-center mx-1" key={index}>
                    <ProtectedImg src={observer.signaturePath} />
                    <small className="text-white d-block">
                      {observer?.observerDetail?.fullName} (Observer)
                    </small>
                  </div>
                ))}
              </div>
            </div>
          )}
          {event.urgentBonusProof !== null && (
            <div className="row justify-content-between mt-3">
              <div className="col-12">
                <div className="text-underline text-white">
                  Urgent Bonus Proof
                </div>
              </div>
              <div className="d-flex col">
                {event.urgentBonusProof?.map((urgentBonusProofPath, index) => (
                  <div className="text-center mr-1" key={index}>
                    <ProtectedImg src={urgentBonusProofPath} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </CustomModal>
      </form>
    </>
  );
};
